import React, { useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import Index from '@/pages/Index';
import Auth from '@/pages/Auth';
import Profile from '@/pages/Profile';
import ToolDetail from '@/pages/ToolDetail';
import ToolConfiguration from '@/pages/ToolConfiguration';
import ToolResults from '@/pages/ToolResults';
import Admin from '@/pages/Admin';
import ROICalculator from '@/pages/ROICalculator';
import Enterprise from '@/pages/Enterprise';
import Integrations from '@/pages/Integrations';
import SurveyTemplates from '@/pages/SurveyTemplates';
import MyGenerations from '@/pages/MyGenerations';
import PublicGeneration from '@/pages/PublicGeneration';
import Analytics from '@/pages/Analytics';
import Bookmarks from '@/pages/Bookmarks';
import NotFound from '@/pages/NotFound';
import { Helmet } from 'react-helmet-async';
import i18n from '@/i18n';
import Terms from '@/pages/Terms';
import Privacy from '@/pages/Privacy';
import PageTracker from '@/components/analytics/PageTracker';

const buildCanonical = (origin: string, pathname: string) => `${origin}${pathname}`;

const LangWrapper: React.FC = () => {
  const { lang } = useParams();
  const location = useLocation();
  const code = (lang || i18n.language || 'en').split('-')[0];

  useEffect(() => {
    if (code && code !== i18n.language) {
      void i18n.changeLanguage(code);
    }
    document.documentElement.setAttribute('lang', code);
    document.documentElement.setAttribute('dir', ['ar', 'he', 'fa', 'ur'].includes(code) ? 'rtl' : 'ltr');
  }, [code]);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const pathNoLang = useMemo(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    if (parts.length && parts[0].length === 2) return '/' + parts.slice(1).join('/');
    return location.pathname;
  }, [location.pathname]);

  const languages = ['en', 'fr'];

  return (
    <>
      <PageTracker />
      <Helmet htmlAttributes={{ lang: code }}>
        <link rel="canonical" href={buildCanonical(origin, location.pathname)} />
        {languages.map(l => (
          <link key={l} rel="alternate" hrefLang={l} href={`${origin}/${l}${pathNoLang}`} />
        ))}
        {/* x-default */}
        <link rel="alternate" hrefLang="x-default" href={`${origin}/en${pathNoLang}`} />
      </Helmet>
      <Routes>
        {/* Non-language-prefixed routes */}
        <Route index element={<Index />} />
        <Route path="auth" element={<Auth />} />
        <Route path="terms" element={<Terms />} />
        <Route path="privacy" element={<Privacy />} />
        <Route path="profile" element={<Profile />} />
        <Route path="tools/:toolId" element={<ToolDetail />} />
        <Route path="tools/:toolId/configuration" element={<ToolConfiguration />} />
        <Route path="tools/:toolId/results" element={<ToolResults />} />
        <Route path="admin" element={<Admin />} />
        {/* <Route path="roi-calculator" element={<ROICalculator />} />
        <Route path="enterprise" element={<Enterprise />} />
        <Route path="integrations" element={<Integrations />} /> */}
        <Route path="survey-templates" element={<SurveyTemplates />} />
        <Route path="survey-templates/:templateId" element={<SurveyTemplates />} />
        <Route path="my-generations" element={<MyGenerations />} />
        <Route path="generations/:generationId/view" element={<Navigate to="my-generations" replace />} />
        <Route path="generations/:generationId/edit" element={<Navigate to="my-generations" replace />} />
        <Route path="share/:slug" element={<PublicGeneration />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="bookmarks" element={<Bookmarks />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/en" replace />} />
      <Route path=":lang/*" element={<LangWrapper />} />
    </Routes>
  );
};

export default AppRoutes;
